/**
 * MP Phase 3 — Audit tab panel for parcel merge/split topology history.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import type { SiteEntity } from '../../../types/site';
import type { SiteBoundaryParcel } from '../../../types/siteBoundary';
import { useStore } from '../../../store';
import { relativeTime } from '../../ui/SaveIndicator';
import { Button } from '../../ui/button';
import type { AuditTelemetryEvent } from '../../../lib/telemetry/auditTelemetry';
import { siteUpdatesFromParcels } from '../../../lib/siteParcelStoreActions';

interface Props {
  site: SiteEntity;
}

interface TopologyRow {
  id: string;
  type: 'parcel-merged' | 'parcel-split';
  timestamp: string;
  beforeCount: number;
  afterCount: number;
  acreageDelta: number;
  beforeParcels: SiteBoundaryParcel[];
}

function parseTopologyRow(event: AuditTelemetryEvent): TopologyRow | null {
  if (event.type !== 'parcel-merged' && event.type !== 'parcel-split') return null;
  const payload = event.payload ?? {};
  if (!payload.enriched) return null;
  const beforeParcels = payload.beforeParcels as SiteBoundaryParcel[] | undefined;
  if (!beforeParcels || !Array.isArray(beforeParcels)) return null;
  return {
    id: String(payload.operationId ?? event.id ?? event.timestamp),
    type: event.type,
    timestamp: event.timestamp,
    beforeCount: Number(payload.beforeCount ?? beforeParcels.length),
    afterCount: Number(payload.afterCount ?? 0),
    acreageDelta: Number(payload.acreageDelta ?? 0),
    beforeParcels,
  };
}

export function ParcelTopologyPanel({ site }: Props) {
  const updateSite = useStore(s => s.updateSite);
  const isAdmin = useStore(s => s.companyRole) === 'admin';
  const [rows, setRows] = useState<TopologyRow[]>([]);
  const [revertingId, setRevertingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { auth, db } = await import('../../../firebase');
        if (!auth.currentUser) {
          setRows([]);
          return;
        }
        const { collection, query, where, orderBy, limit, getDocs } = await import('firebase/firestore');
        const q = query(
          collection(db, 'auditEvents'),
          where('siteId', '==', site.id),
          where('type', 'in', ['parcel-merged', 'parcel-split']),
          orderBy('timestamp', 'desc'),
          limit(20),
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        const parsed = snap.docs
          .map(d => parseTopologyRow({ id: d.id, ...d.data() } as AuditTelemetryEvent))
          .filter((r): r is TopologyRow => r != null);
        setRows(parsed);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [site.id, site.siteBoundaries?.length, site.updatedAt]);

  const handleRevert = useCallback(async (row: TopologyRow) => {
    if (!isAdmin) return;
    setRevertingId(row.id);
    try {
      await updateSite(site.id, siteUpdatesFromParcels(row.beforeParcels));
    } finally {
      setRevertingId(null);
    }
  }, [isAdmin, site.id, updateSite]);

  if (rows.length === 0) return null;

  return (
    <section className="border border-border p-4 space-y-2" data-testid="parcel-topology-panel">
      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary">
        Parcel topology history
      </h3>
      <ul className="space-y-2">
        {rows.map(row => (
          <li key={row.id} className="text-xs border border-border-subtle p-2 flex flex-wrap items-center gap-2 justify-between">
            <div>
              <span className="font-semibold">
                Parcel topology changed {relativeTime(Date.parse(row.timestamp))}
              </span>
              {' · '}
              {row.beforeCount} → {row.afterCount} parcels
              {' · '}
              {row.acreageDelta >= 0 ? '+' : ''}{row.acreageDelta.toFixed(1)} ac
            </div>
            {isAdmin && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={revertingId === row.id}
                onClick={() => void handleRevert(row)}
                aria-label="Revert parcel topology change"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Revert
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
